// Wave 3 (2026-07-12): the consolidated "Finish your order" screen.
//
// Once the customer has at least one saved spot and is not mid-edit, the
// approval choice, paid order add-ons, the back cross-sell, the
// complete-the-set card and the notes field all used to sit in separate
// accordion steps / positions, chained together by Continue buttons that
// gated nothing. This screen replaces that chain: one titled group, in a
// fixed vertical order, each card keeping its own existing handler and
// pricing path. Nothing here changes the cart-line properties - the App
// still owns all the state and pricing.ts still serialises it.
//
// Vertical order:
//   a. Approval  - a FREE choice, visually distinct from the paid upsells;
//      impossible to miss because it gates production holds.
//   b. Paid add-ons (gift box / rush).
//   c. Back cross-sell (renders only when its own eligibility holds).
//   d. Complete-the-set / second-item card (post-ATC, only when eligible).
//   e. Customer notes, folded into a collapsed "Add a note" disclosure.
//
// Cards that aren't eligible simply don't render - no empty slots.

import * as React from "react";
import type { Config, Upsell } from "../types";
import type { ApprovalChoice, Spot } from "../pricing";
import type { EmbroideryDraft } from "./PlacementSection";
import { flowCopy } from "../copy";
import { emitAnalytics, type AnalyticsEventDetail } from "../analytics";
import { ApprovalControls } from "./ApprovalSection";
import { OrderAddOnsControls } from "./OrderAddOnsSection";
import { BackCrossSellSection } from "./BackCrossSellSection";
import { NotesField } from "./NotesSection";

type AnalyticsBase = Pick<
  AnalyticsEventDetail,
  "productGid" | "productHandle" | "variantId"
>;

export function FinishSection({
  config,
  // a. approval
  approvalChoice,
  onChooseApproval,
  approvalAddonId,
  onChooseApprovalAddon,
  // b. paid add-ons
  orderAddons,
  orderAddOnIds,
  onToggleOrderAddOn,
  // c. back cross-sell
  spots,
  draft,
  lockedSlugs,
  onPickBack,
  // d. complete-the-set (built by the parent, rendered here when present)
  secondItemCard,
  // e. notes
  notesValue,
  onNotesChange,
  analyticsBase,
}: {
  config: Config;
  approvalChoice: ApprovalChoice;
  onChooseApproval: (c: Exclude<ApprovalChoice, null>) => void;
  approvalAddonId: string | null;
  onChooseApprovalAddon: (upsellId: string) => void;
  orderAddons: Upsell[];
  orderAddOnIds: string[];
  onToggleOrderAddOn: (id: string) => void;
  spots: Spot[];
  draft: EmbroideryDraft | null;
  lockedSlugs: Map<string, string>;
  onPickBack: (next: EmbroideryDraft) => void;
  secondItemCard: React.ReactNode;
  notesValue: string;
  onNotesChange: (v: string) => void;
  analyticsBase: AnalyticsBase;
}) {
  // Notes disclosure: collapsed by default, but pre-opened if the customer
  // already typed something (so a persisted / edited note stays visible).
  const [notesOpen, setNotesOpen] = React.useState(
    () => notesValue.trim().length > 0,
  );

  // Fire finish_addon_accept only on the OFF -> ON transition, so a toggle
  // off (or a re-render) never double-counts. The pricing path is
  // untouched: we still call the parent's toggle handler afterwards.
  const handleAddonToggle = (id: string) => {
    if (!orderAddOnIds.includes(id)) {
      const u = orderAddons.find((x) => x.id === id);
      if (u) {
        emitAnalytics("finish_addon_accept", {
          ...analyticsBase,
          addonKind: u.kind,
          upsellId: u.id,
          upsellLabel: u.label,
          upsellPriceCents: u.priceCents,
        });
      }
    }
    onToggleOrderAddOn(id);
  };

  const notesTrimmed = notesValue.trim();
  const notesSummary = notesTrimmed
    ? notesTrimmed.length > 60
      ? `${notesTrimmed.slice(0, 60)}…`
      : notesTrimmed
    : "Anything we should know before we stitch?";

  return (
    <div className="rk-pc-finish">
      <div className="rk-pc-finish__head">
        <span className="rk-pc-finish__title">Finish your order</span>
        <span className="rk-pc-finish__sub">
          One last look, then add it to your cart.
        </span>
      </div>

      {/* a. Approval - a free choice, styled distinct from the paid
          upsells and always expanded so it can't be missed. */}
      <section className="rk-pc-sec is-active rk-pc-finish__approval">
        <div className="rk-pc-sec__head" role="heading" aria-level={2}>
          <span className="rk-pc-sec__num" aria-hidden>
            ✓
          </span>
          <span className="rk-pc-sec__heading">
            <span className="rk-pc-sec__title">
              {flowCopy(config, "approvalTitle", "Design approval")}
            </span>
            <span className="rk-pc-sec__summary">
              A free choice - how should we proceed?
            </span>
          </span>
          <span className="rk-pc-finish__free-pill">Free</span>
        </div>
        <ApprovalControls
          choice={approvalChoice}
          onChoose={onChooseApproval}
          config={config}
          addonId={approvalAddonId}
          onChooseAddon={onChooseApprovalAddon}
        />
      </section>

      {/* b. Paid add-ons: gift box + rush (self-hides when none apply). */}
      <OrderAddOnsControls
        config={config}
        upsells={orderAddons}
        pickedIds={orderAddOnIds}
        onToggle={handleAddonToggle}
      />

      {/* c. Back cross-sell (self-gates on its own eligibility). */}
      <BackCrossSellSection
        config={config}
        spots={spots}
        draft={draft}
        lockedSlugs={lockedSlugs}
        onPickBack={onPickBack}
      />

      {/* d. Complete-the-set / second-item card, built + gated by the
          parent (only present after add-to-cart when a candidate exists). */}
      {secondItemCard}

      {/* e. Notes, folded into a collapsed disclosure. */}
      <section className="rk-pc-sec rk-pc-finish__notes">
        <button
          type="button"
          className="rk-pc-sec__head"
          aria-expanded={notesOpen}
          onClick={() => setNotesOpen((o) => !o)}
        >
          <span className="rk-pc-sec__num" aria-hidden>
            ✎
          </span>
          <span className="rk-pc-sec__heading">
            <span className="rk-pc-sec__title">Add a note (optional)</span>
            <span className="rk-pc-sec__summary">{notesSummary}</span>
          </span>
          <span className="rk-pc-sec__chevron" aria-hidden>
            {notesOpen ? "▾" : "▸"}
          </span>
        </button>
        <div className="rk-pc-collapse" data-open={notesOpen}>
          <div className="rk-pc-collapse__clip">
            <div className="rk-pc-sec__body">
              <NotesField value={notesValue} onChange={onNotesChange} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
